import {
  AppBar,
  Box,
  Button,
  Toolbar,
  Typography,
  Avatar,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Badge,
} from "@mui/material";
import {
  LoginOutlined,
  LogoutOutlined,
  PersonOutline,
  MenuOutlined,
  LanguageOutlined,
  CloseOutlined,
  SettingsOutlined,
} from "@mui/icons-material";
import { Auth, Hub } from "aws-amplify";
import React from "react";
import { useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { useTheme as useAppTheme } from "../contexts/ThemeContext";
import { GameInfoDialog } from "./GameInfoDialog";
import { AuthDialog } from "../components/AuthDialog";
import { PersonalSettingsDialog } from "../components/PersonalSettingsDialog";
import { restoreLocalStateFromCloud } from "../utils/playerState";

export function ViewWrapper(props: { children?: React.ReactNode }) {
  const location = useLocation();
  const [hideInfo, setHideInfo] = React.useState<boolean>(
    Boolean(localStorage.getItem("hideInfo")) || false
  );
  const [offline, setOffline] = React.useState(false);
  const { t, i18n } = useTranslation();
  const { themeConfig } = useAppTheme();

  // Only show info dialog on homepage, not in game pages
  const isGamePage = location.pathname.startsWith("/game/");
  const shouldShowInfo = !hideInfo && !isGamePage;
  const [authDialogOpen, setAuthDialogOpen] = React.useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = React.useState(false);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [user, setUser] = React.useState<{
    attributes?: { email?: string };
  } | null>(null);

  console.log("location is: ", location);

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
  };

  const availLangArr = [
    { title: "Deutsch", key: "de", code: "DE", flag: "🇩🇪" },
    { title: "English", key: "en", code: "US", flag: "🇺🇸" },
  ];

  React.useEffect(() => {
    // Check initial online status
    setOffline(!navigator.onLine);

    // Listen to network status changes using browser API
    const handleOnline = () => {
      console.log("🌐 Network: ONLINE");
      setOffline(false);
    };

    const handleOffline = () => {
      console.log("📵 Network: OFFLINE");
      setOffline(true);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Cleanup listeners
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const refreshAuthState = React.useCallback(async () => {
    try {
      const currentUser = await Auth.currentAuthenticatedUser();
      setUser(currentUser);
      await restoreLocalStateFromCloud();
      // Notify other components that cloud sync is complete
      window.dispatchEvent(new Event("cloudSyncComplete"));
      console.log("✅ Cloud sync complete, event dispatched");
    } catch (error) {
      // Auth not configured or user not logged in - this is OK
      console.log(
        "ℹ️ [ViewWrapper] No authenticated user (this is normal for anonymous play)"
      );
      setUser(null);
      // Still dispatch event for non-authenticated users
      window.dispatchEvent(new Event("cloudSyncComplete"));
    }
  }, []);

  React.useEffect(() => {
    refreshAuthState();
    const authListener = Hub.listen("auth", async (hubData) => {
      const { event } = hubData.payload;
      if (event === "signIn") {
        await refreshAuthState();
      }
      if (event === "signOut") {
        setUser(null);
      }
    });
    return () => {
      authListener();
    };
  }, [refreshAuthState]);

  const handleSignOut = async () => {
    try {
      await Auth.signOut();
    } catch (error) {
      console.error("logout failed", error);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
      }}
    >
      {shouldShowInfo && <GameInfoDialog onClose={setHideInfo} />}
      {offline && (
        <Box
          sx={{
            backgroundColor: "red",
            width: "100%",
            textAlign: "center",

            display: "flex",
            justifyContent: "center",
            flexDirection: "column",
          }}
        >
          <Typography variant="overline" fontWeight={"bold"}>
            {t("Toolbar.noServerConnection")}
          </Typography>
          <Typography variant="caption" gutterBottom>
            {t("Toolbar.pleaseCheckYourConnection")}
          </Typography>
        </Box>
      )}
      <AppBar
        position="static"
        sx={{
          background: themeConfig.appBar.background,
          borderBottom: "3px solid",
          borderColor: "divider",
          boxShadow: 3,
          zIndex: 1100, // Ensure AppBar is above confetti
        }}
      >
        <Toolbar sx={{ minHeight: 64 }}>
          {/* <IconButton
              size="large"
              edge="start"
              color="inherit"
              aria-label="menu"
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton> */}
          {location.pathname !== "/" && (
            <Button
              href="/"
              sx={{
                float: "left",
                margin: 1,
                color: "primary.main",
                fontWeight: 600,
                "&:hover": {
                  bgcolor: "action.hover",
                },
              }}
            >
              {t("MenuItems.myGames")}
            </Button>
          )}
          <Button
            href="/info"
            sx={{
              float: "left",
              margin: 1,
              color: "primary.main",
              fontWeight: 600,
              "&:hover": {
                bgcolor: "action.hover",
              },
            }}
          >
            {t("MenuItems.gameRules")}
          </Button>
          <Box sx={{ flexGrow: 1 }} />

          <IconButton
            onClick={() => setDrawerOpen(true)}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              color: "primary.main",
            }}
          >
            <Badge
              color="primary"
              variant="dot"
              invisible={!user}
              sx={{
                "& .MuiBadge-dot": {
                  right: 3,
                  top: 3,
                },
              }}
            >
              <MenuOutlined />
            </Badge>
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* User Menu Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: 320,
            borderRadius: "16px 0 0 16px",
          },
        }}
      >
        {/* Drawer Header */}
        <Box
          sx={{
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            {t("MenuItems.menu")}
          </Typography>
          <IconButton onClick={() => setDrawerOpen(false)} size="small">
            <CloseOutlined />
          </IconButton>
        </Box>

        {/* User Section */}
        <Box sx={{ p: 2 }}>
          {user?.attributes?.email ? (
            <Box
              sx={{
                bgcolor: "primary.50",
                borderRadius: 2,
                p: 2,
                mb: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <Avatar
                  sx={{
                    bgcolor: "primary.main",
                    width: 48,
                    height: 48,
                    mr: 2,
                  }}
                >
                  <PersonOutline />
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t("MenuItems.loggedInAs")}
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {user.attributes.email}
                  </Typography>
                </Box>
              </Box>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<LogoutOutlined />}
                onClick={() => {
                  handleSignOut();
                  setDrawerOpen(false);
                }}
                sx={{
                  textTransform: "none",
                  borderRadius: 1.5,
                }}
              >
                {t("MenuItems.logout")}
              </Button>
            </Box>
          ) : (
            <Button
              fullWidth
              variant="contained"
              size="large"
              startIcon={<LoginOutlined />}
              onClick={() => {
                setAuthDialogOpen(true);
                setDrawerOpen(false);
              }}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                py: 1.5,
                mb: 2,
              }}
            >
              {t("MenuItems.login")}
            </Button>
          )}

          {/* Personal Settings Button */}
          <Button
            fullWidth
            variant="outlined"
            startIcon={<SettingsOutlined />}
            onClick={() => {
              window.location.href = "/settings";
              setDrawerOpen(false);
            }}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              py: 1.5,
            }}
          >
            {t("PersonalSettings.title")}
          </Button>
        </Box>

        <Divider />

        {/* Language Selection */}
        <List sx={{ px: 1 }}>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 40 }}>
              <LanguageOutlined color="action" />
            </ListItemIcon>
            <ListItemText
              primary="Sprache / Language"
              primaryTypographyProps={{
                variant: "body2",
                fontWeight: 600,
                color: "text.secondary",
              }}
            />
          </ListItem>
          {availLangArr.map((lang) => (
            <ListItemButton
              key={lang.key}
              selected={i18n.language === lang.key}
              onClick={() => {
                changeLanguage(lang.key);
              }}
              sx={{
                borderRadius: 2,
                mx: 1,
                mb: 0.5,
                "&.Mui-selected": {
                  bgcolor: "primary.50",
                  "&:hover": {
                    bgcolor: "primary.100",
                  },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Typography fontSize="1.5rem">{lang.flag}</Typography>
              </ListItemIcon>
              <ListItemText
                primary={lang.title}
                primaryTypographyProps={{
                  fontWeight: i18n.language === lang.key ? 600 : 400,
                }}
              />
              {i18n.language === lang.key && (
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: "primary.main",
                  }}
                />
              )}
            </ListItemButton>
          ))}
        </List>
      </Drawer>

      <Box>{props.children}</Box>
      <AuthDialog
        open={authDialogOpen}
        onClose={() => setAuthDialogOpen(false)}
        onAuthenticated={refreshAuthState}
      />
      <PersonalSettingsDialog
        open={settingsDialogOpen}
        onClose={() => setSettingsDialogOpen(false)}
      />
    </Box>
  );
}
